import { isImportMetaEnvFlagEnabled } from '../lib/env';
import { type ItemDoc, itemSchema } from './collections/items';
import { type ProcessDoc, processSchema } from './collections/processes';
import {
  type ProcessItemSummaryDoc,
  processItemSummarySchema,
} from './collections/processItemSummaries';
import { type WorkspaceDoc, workspaceSchema } from './collections/workspaces';
import {
  isRxSchemaMismatchError,
  readLocalCacheVersion,
  shouldResetForLocalCacheVersion,
  writeCurrentLocalCacheVersion,
} from './localCacheVersion';
import { clearCheckpointFallbacks } from './replication/checkpointFallback';
import {
  addRxPlugin,
  createRxDatabase,
  removeRxDatabase,
  type RxCollection,
  type RxDatabase,
  type RxStorage,
} from 'rxdb';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';

const isDev = isImportMetaEnvFlagEnabled(import.meta.env?.DEV);

if (isDev) {
  addRxPlugin(RxDBDevModePlugin);
}

addRxPlugin(RxDBMigrationSchemaPlugin);

const getStorage = () =>
  isDev
    ? wrappedValidateAjvStorage({ storage: getRxStorageDexie() })
    : getRxStorageDexie();

export type ClewCollections = {
  items: RxCollection<ItemDoc>;
  processes: RxCollection<ProcessDoc>;
  processItemSummaries: RxCollection<ProcessItemSummaryDoc>;
  workspaces: RxCollection<WorkspaceDoc>;
};

export type ClewDatabase = RxDatabase<ClewCollections>;

let dbPromise: null | Promise<ClewDatabase> = null;
let dbUserId: null | string = null;

const databaseName = (userId: string) => `clew-${userId}`;

const removeLocalDatabase = async (
  userId: string,
  storage: RxStorage<unknown, unknown>,
) => {
  clearCheckpointFallbacks(userId);
  await removeRxDatabase(databaseName(userId), storage, false);
};

const createDatabaseInstance = async (
  userId: string,
  storage: RxStorage<unknown, unknown>,
): Promise<ClewDatabase> => {
  const db = await createRxDatabase<ClewCollections>({
    ignoreDuplicate: false,
    multiInstance: false,
    name: databaseName(userId),
    storage,
  });

  try {
    await db.addCollections({
      items: {
        migrationStrategies: {
          // 0 → 1: rxdbFromZod now emits RxDB-compatible schemas for item ids
          // and nullable metadata. Document data is already compatible.
          '1': (doc) => doc,
        },
        schema: itemSchema,
      },
      processes: {
        migrationStrategies: {
          // 0 → 1: allow legacy UUID process/workspace ids in the RxDB schema.
          // Stored document layout is unchanged.
          '1': (doc) => doc,
        },
        schema: processSchema,
      },
      processItemSummaries: {
        schema: processItemSummarySchema,
      },
      workspaces: {
        migrationStrategies: {
          // 0 → 1: schema shape changed via rxdbFromZod refactor;
          // data layout is compatible so this is a no-op pass-through.
          '1': (doc) => doc,
        },
        schema: workspaceSchema,
      },
    });
  } catch (error) {
    try {
      await db.close();
    } catch {
      // The reset path below can still remove the persisted cache.
    }

    throw error;
  }

  return db;
};

const initializeDatabase = async (userId: string): Promise<ClewDatabase> => {
  const storage = getStorage();
  const storedVersion = readLocalCacheVersion(userId);

  if (shouldResetForLocalCacheVersion(storedVersion)) {
    await removeLocalDatabase(userId, storage);
  }

  try {
    const db = await createDatabaseInstance(userId, storage);
    writeCurrentLocalCacheVersion(userId);
    return db;
  } catch (error) {
    if (!isRxSchemaMismatchError(error)) {
      throw error;
    }

    await removeLocalDatabase(userId, storage);
    const db = await createDatabaseInstance(userId, storage);
    writeCurrentLocalCacheVersion(userId);
    return db;
  }
};

export const getDatabase = (userId: string): Promise<ClewDatabase> => {
  if (dbPromise && dbUserId === userId) {
    return dbPromise;
  }

  dbUserId = userId;
  dbPromise = (async () => {
    try {
      return await initializeDatabase(userId);
    } catch (error) {
      if (dbUserId === userId) {
        dbPromise = null;
        dbUserId = null;
      }

      throw error;
    }
  })();

  return dbPromise;
};

export const dropDatabase = async () => {
  const pending = dbPromise;
  const userId = dbUserId;
  if (!pending) {
    return;
  }

  dbPromise = null;
  dbUserId = null;
  if (userId) {
    clearCheckpointFallbacks(userId);
  }

  const db = await pending;
  await db.remove();
};
